import type { PushKeyView } from '@crowdex/agent-contracts/api';
import { apiFetch } from '@crowdex/host-kit/renderer/http';
import { isElectronDesktop } from '@/lib/electron-bridge';

/*
 * A browser's system notifications: the server pushes them (Web Push) to this browser's service
 * worker (`public/sw.js`), which shows them whether Crowdex is open or not. Desktop has its own.
 */

/** What this browser can do: receive them, or not, or only once installed (iOS). */
export type PushSupport = 'supported' | 'install-first' | 'unsupported';

export function pushSupport(): PushSupport {
  if (typeof window === 'undefined' || isElectronDesktop()) return 'unsupported';
  if ('serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window)
    return 'supported';
  // iOS and iPadOS offer Web Push to web apps added to the home screen only.
  return /iP(hone|ad|od)/.test(navigator.userAgent) ? 'install-first' : 'unsupported';
}

/** Whether the user allowed this browser's notifications ('default': not asked yet). */
export function pushPermission(): NotificationPermission {
  return pushSupport() === 'supported' ? Notification.permission : 'denied';
}

const json = (body: unknown): RequestInit => ({
  headers: { 'content-type': 'application/json' },
  body: JSON.stringify(body),
});

/**
 * Subscribes this browser to the user's notifications (asking for permission first, which
 * browsers allow from a user's gesture only), and tells the server. True once subscribed.
 */
export async function subscribeToPush(): Promise<boolean> {
  if (pushSupport() !== 'supported') return false;
  if ((await Notification.requestPermission()) !== 'granted') return false;
  const registration = await navigator.serviceWorker.ready;
  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: (await apiFetch<PushKeyView>('/api/push/key')).publicKey,
    }));
  await apiFetch('/api/push/subscription', { method: 'PUT', ...json(subscription.toJSON()) });
  return true;
}

/** This browser no longer receives the user's notifications. */
export async function unsubscribeFromPush(): Promise<void> {
  if (pushSupport() !== 'supported') return;
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (subscription === null) return;
  await apiFetch('/api/push/subscription', {
    method: 'DELETE',
    ...json({ endpoint: subscription.endpoint }),
  });
  await subscription.unsubscribe();
}

/** Whether this browser is subscribed. */
export async function pushSubscribed(): Promise<boolean> {
  if (pushPermission() !== 'granted') return false;
  const registration = await navigator.serviceWorker.ready;
  return (await registration.pushManager.getSubscription()) !== null;
}

function subscribeQuietly(): void {
  void subscribeToPush().catch((error: unknown) => {
    console.warn('This browser was not subscribed to notifications.', error);
  });
}

/** Keeps the server told of this browser's subscription once allowed (signed in again, say). */
export function syncPushSubscription(): void {
  if (pushPermission() === 'granted') subscribeQuietly();
}

let asked = false;

/**
 * Browsers start with notifications neither allowed nor denied, and ask only from a user's
 * gesture: after the first notification told in the app, the next click asks once.
 */
export function askForPushOnce(): void {
  if (asked || pushPermission() !== 'default') return;
  asked = true;
  window.addEventListener('pointerdown', subscribeQuietly, { once: true });
}

/** A clicked notification to open: its event, and its conversation when it has one. */
export type OpenedNotification = { id: string; threadId: string | null };

/** The service worker's message asking to open a clicked notification's event. */
export function openedNotification(message: unknown): OpenedNotification | null {
  if (typeof message !== 'object' || message === null) return null;
  const { type, id, threadId } = message as { type?: unknown; id?: unknown; threadId?: unknown };
  if (type !== 'crowdex:open-notification' || typeof id !== 'string') return null;
  return { id, threadId: typeof threadId === 'string' ? threadId : null };
}
